/**
 * A project's whiteboard is open when the URL says `?board=<conversation id>` — a refresh lands back on it, Back
 * leaves it, and a link to it can be shared with the room. Which of the room's boards (owner, 2026-10-30: 新增不同的
 * board … 選擇 board) rides beside it as `&wb=<board id>`; without it, the room's first board, `main`. The rest of the
 * app's URL state (urlState.ts) keeps params it does not own, so the board rides on top of whatever is selected
 * underneath (as a game does — games/gameNav.ts).
 */
const PARAM = 'board'
const WHICH = 'wb'
const EVENT = 'avg-board-nav'
export const MAIN_BOARD = 'main'
const BOARD_ID = /^[a-z0-9]{4,24}$/
/** This device remembers the board last open in each room, so the Whiteboard button goes back to it. */
const LAST = 'avg.board.last.'

export function openBoardId(): string | null {
  const id = new URLSearchParams(window.location.search).get(PARAM)
  return id && /^[0-9a-f]{16}$/.test(id) ? id : null
}

/** Which of the room's boards is open. */
export function openBoardSel(): string {
  const b = new URLSearchParams(window.location.search).get(WHICH)
  return b && BOARD_ID.test(b) ? b : MAIN_BOARD
}

export function lastBoard(conv: string): string {
  try {
    const b = localStorage.getItem(LAST + conv)
    return b && BOARD_ID.test(b) ? b : MAIN_BOARD
  } catch {
    return MAIN_BOARD
  }
}

export function rememberBoard(conv: string, board: string): void {
  try {
    if (board === MAIN_BOARD) localStorage.removeItem(LAST + conv)
    else localStorage.setItem(LAST + conv, board)
  } catch {
    /* a private window: the room's first board next time */
  }
}

function go(q: URLSearchParams, push: boolean): void {
  const s = q.toString()
  const url = `${window.location.pathname}${s ? `?${s}` : ''}${window.location.hash}`
  if (push) window.history.pushState(window.history.state, '', url)
  else window.history.replaceState(window.history.state, '', url)
  window.dispatchEvent(new Event(EVENT))
}

/** Open the room's whiteboard — the board this device had open there last. */
export function openBoard(id: string): void {
  const q = new URLSearchParams(window.location.search)
  if (q.get(PARAM) === id) return
  q.set(PARAM, id)
  const b = lastBoard(id)
  if (b === MAIN_BOARD) q.delete(WHICH)
  else q.set(WHICH, b)
  go(q, true)
}

/**
 * Open the room's whiteboard on one board — a message's board chip (owner, 2026-10-30: a message sent from a
 * board's chat names the board). From the chat, a new page (Back returns to the chat); already on the room's
 * whiteboard, in place.
 */
export function openBoardOn(conv: string, board: string): void {
  if (!BOARD_ID.test(board)) return
  rememberBoard(conv, board)
  const q = new URLSearchParams(window.location.search)
  const was = q.get(PARAM)
  q.set(PARAM, conv)
  if (board === MAIN_BOARD) q.delete(WHICH)
  else q.set(WHICH, board)
  go(q, was !== conv)
}

/** Another of the room's boards, in place: Back still leaves the whiteboard rather than walking through boards. */
export function selectBoard(board: string): void {
  const q = new URLSearchParams(window.location.search)
  const conv = q.get(PARAM)
  if (!conv) return
  rememberBoard(conv, board)
  if ((q.get(WHICH) ?? MAIN_BOARD) === board) return
  if (board === MAIN_BOARD) q.delete(WHICH)
  else q.set(WHICH, board)
  go(q, false)
}

export function closeBoard(): void {
  const q = new URLSearchParams(window.location.search)
  if (!q.has(PARAM)) return
  q.delete(PARAM)
  q.delete(WHICH)
  go(q, false)
}

/** Call `fn` whenever the open board may have changed (opened, closed, another picked, Back/Forward). */
export function onBoardNav(fn: () => void): () => void {
  window.addEventListener('popstate', fn)
  window.addEventListener(EVENT, fn)
  return () => {
    window.removeEventListener('popstate', fn)
    window.removeEventListener(EVENT, fn)
  }
}
